/**
 * Separate trailing punctuation (e.g. commas, periods, semicolons, colons,
 * parentheses) from a token so that "1,000,000," becomes ["1,000,000", ","].
 */
const splitTrailingPunctuation = (
  token: string
): { core: string; suffix: string } => {
  const match = token.match(/^(.*?)([.,;:)\]]+)$/);
  if (match) return { core: match[1], suffix: match[2] };
  return { core: token, suffix: "" };
};

/**
 * Try to parse a single whitespace-delimited token as a number.
 *
 * Strips a leading "$" and any commas, then checks whether the remainder is a
 * plain non-negative number (integer or decimal).  Returns the parsed number on
 * success or `null` if the token is not purely numeric.
 */
const parseNumericToken = (token: string): number | null => {
  const stripped = token.replace(/^\$/, "").replace(/,/g, "");
  if (stripped === "" || !/^\d+(\.\d+)?$/.test(stripped)) return null;
  return Number(stripped);
};

/**
 * Format a numeric token with comma-separated thousands and a leading "$".
 * Preserves any decimal portion exactly as it appeared in the original string.
 */
const formatNumber = (token: string): string => {
  const stripped = token.replace(/^\$/, "").replace(/,/g, "");
  const [integerPart, decimalPart] = stripped.split(".");
  const withCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart !== undefined
    ? `$${withCommas}.${decimalPart}`
    : `$${withCommas}`;
};

/**
 * Format monetary values inside a free-text string.
 *
 * Splits the input on whitespace, and for each token that looks like a bare
 * number (optionally prefixed with "$" and/or containing commas), reformats it
 * as "$X,XXX,XXX".  Trailing punctuation (commas, periods, etc.) is preserved.
 * All other tokens are left untouched.
 *
 * Examples:
 *   "1000000"                        → "$1,000,000"
 *   "1000 per occurrence"            → "$1,000 per occurrence"
 *   "USD 25000 any one occurrence"   → "USD $25,000 any one occurrence"
 *   "72 hours"                       → "72 hours"  (72 is below threshold)
 *   "5% of TIV"                      → "5% of TIV" (% makes it non-numeric)
 *
 * Numbers below 1,000 that appear alongside other words are left alone so that
 * incidental small numbers (like "72" in "72 hours") are not converted.  A
 * standalone value of e.g. "500" (the entire input) IS formatted, since the
 * user clearly typed just a number.
 */
export const formatMonetaryValue = (raw: string): string => {
  const trimmed = raw.trim();
  if (trimmed === "") return trimmed;

  const tokens = trimmed.split(/\s+/);
  const isMultiToken = tokens.length > 1;

  const formatted = tokens.map((token) => {
    // Peel off trailing punctuation so "1,000,000," → core "1,000,000" + suffix ","
    const { core, suffix } = splitTrailingPunctuation(token);

    const num = parseNumericToken(core);
    if (num === null) return token;

    // In multi-token strings, only format numbers >= 1000 to avoid touching
    // incidental small numbers like "72" in "72 hours".
    if (isMultiToken && num < 1000) return token;

    return formatNumber(core) + suffix;
  });

  return formatted.join(" ");
};
